document.addEventListener('DOMContentLoaded', () => {
  validateCart();
});

/**
 * Main validation function that fetches cart once and runs all validations
 */
const validateCart = () => {
  // Fetch current cart data once
  fetch(`${routes.cart_url}.js`)
    .then((response) => response.json())
    .then((cart) => {
      // Run validations with the cart data
      const shouldClearCart = validateCartWithOnlyZeroDollarItems(cart);

      if (shouldClearCart) {
        clearCart();
        return;
      }

      // Check for Component items and clear entire cart if any are found
      if (hasComponentItems(cart)) {
        clearCart();
        return;
      }
    })
    .catch((error) => {
      console.error('Error fetching cart data:', error);
    });
};

// Make validateCart globally available
window.validateCart = validateCart;

/**
 * Clears the entire cart and reloads the page
 */
const clearCart = () => {
  fetch(`${routes.cart_url}/clear.js`, {
    ...fetchConfig(),
    method: 'POST',
  })
    .then((response) => response.json())
    .then(() => {
      // Reload the page to reflect changes
      window.location.reload();
    })
    .catch((error) => {
      console.error('Error clearing cart:', error);
    });
};

/**
 * Validates if cart has items but zero total price
 * If true, returns true to indicate the cart should be cleared
 * @param {Object} cart - The cart data
 * @returns {boolean} - Whether the cart should be cleared
 */
const validateCartWithOnlyZeroDollarItems = (cart) => {
  // Check if cart has items but total price is 0
  if (cart.items.length > 0 && cart.total_price === 0) {
    return true;
  }

  return false;
};

/**
 * Removes items from the cart one at a time to handle shifting indices
 */
const removeItemsSequentially = (items) => {
  // Clone the array to avoid modifying the original
  const itemsToRemove = [...items];

  // Exit condition - no more items
  if (itemsToRemove.length === 0) {
    return Promise.resolve();
  }

  // Take the first item from the array
  const item = itemsToRemove.shift();

  // Remove this item
  return fetch(`${routes.cart_change_url}`, {
    ...fetchConfig(),
    body: JSON.stringify({
      line: item.line,
      quantity: 0,
      sections: ['cart-drawer', 'cart-icon-bubble'],
    }),
  })
    .then((response) => response.json())
    .then((state) => {
      // After removing an item, update line numbers for remaining items
      itemsToRemove.forEach((remainingItem) => {
        if (remainingItem.line > item.line) {
          remainingItem.line -= 1;
        }
      });

      // Continue with the rest of the items
      return removeItemsSequentially(itemsToRemove);
    })
    .catch((error) => {
      console.error(`Error removing item at line ${item.line}:`, error);
      // Continue with the rest even if there's an error
      return removeItemsSequentially(itemsToRemove);
    });
};

/**
 * Updates cart sections after modifications
 */
const updateCartSections = () => {
  fetch(`${routes.cart_update_url}`, {
    ...fetchConfig(),
    body: JSON.stringify({
      sections: ['cart-drawer', 'cart-icon-bubble'],
    }),
  })
    .then((response) => response.json())
    .then((state) => {
      // Find and update cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.renderContents(state);
      }
    })
    .catch((error) => {
      console.error('Error updating cart sections:', error);
    });
};

/**
 * Finds items with product_type of "Component" and prepares them for removal
 * @param {Object} cart - The cart data
 * @returns {Array} - Array of items to remove
 */
const findStandaloneComponentItems = (cart) => {
  const componentItems = cart.items.filter((item) => item.product_type === 'Component');

  if (componentItems.length > 0) {
    return componentItems.map((item) => {
      // Find the 1-indexed position in the cart
      const lineIndex = cart.items.findIndex((cartItem) => cartItem.key === item.key) + 1;

      return {
        id: 'component',
        variantId: item.variant_id,
        line: lineIndex,
        quantity: 0,
      };
    });
  }

  return [];
};

/**
 * Checks if cart has any items with product_type of "Component"
 * @param {Object} cart - The cart data
 * @returns {boolean} - Whether the cart has any Component items
 */
const hasComponentItems = (cart) => {
  // Check DOM elements in the cart drawer if they exist
  const itemsInCartDrawer = document.querySelectorAll('.cart-drawer-item');
  const componentItemsInDOM = Array.from(itemsInCartDrawer).some((item) => {
    const productType = item.getAttribute('data-is-component');
    const isSoldIndividually = item.getAttribute('data-component-sold-individually');

    // Return true if this is a Component item that shouldn't be sold individually
    return productType == 'Component' && isSoldIndividually == 'false';
  });

  // Return true if we found component items in either the cart data or DOM
  return componentItemsInDOM;
};
